/**
 * Admin Settings Page
 * Platform-wide configuration for admin users
 *
 * Features:
 * - Reranking settings (Cohere Rerank API)
 * - Future: Other platform settings
 */

import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../../hooks/useAdminAuth';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../hooks/useToast';
import {
  Settings as SettingsIcon,
  Zap,
  Save,
  RefreshCw,
  Info,
  DollarSign,
  TrendingUp,
  Sliders
} from 'lucide-react';
import { API_BASE_URL } from '../../../utils/constants';

interface RerankingSettings {
  reranking_enabled: boolean;
  reranking_model: string;
  reranking_threshold: number;
  reranking_top_k: number;
}

export const AdminSettings: React.FC = () => {
  const { admin } = useAdminAuth();
  const { toast } = useToast();

  const [settings, setSettings] = useState<RerankingSettings>({
    reranking_enabled: false,
    reranking_model: 'rerank-v3.5',
    reranking_threshold: 15,
    reranking_top_k: 10
  });

  const [originalSettings, setOriginalSettings] = useState<RerankingSettings>(settings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // Check if settings have changed from original
    const changed =
      settings.reranking_enabled !== originalSettings.reranking_enabled ||
      settings.reranking_model !== originalSettings.reranking_model ||
      settings.reranking_threshold !== originalSettings.reranking_threshold ||
      settings.reranking_top_k !== originalSettings.reranking_top_k;
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/settings/reranking`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setOriginalSettings(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load settings',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/admin/settings/reranking`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setOriginalSettings(data);
        toast({
          title: 'Success',
          description: 'Settings updated successfully',
          variant: 'success'
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to update settings',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
  };

  const estimateCostPerSearch = () => {
    return settings.reranking_enabled ? '$0.002' : '$0.000';
  };

  const estimateMonthlyCost = (searches: number) => {
    if (!settings.reranking_enabled) return '$0.00';
    return `$${(searches * 0.002).toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <SettingsIcon className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Platform Settings</h1>
          </div>
          <p className="text-gray-600">Configure platform-wide features and optimizations</p>
        </div>

        {/* Reranking Settings Card */}
        <Card className="mb-6">
          <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-semibold text-gray-900">Semantic Reranking</h2>
                </div>
                <p className="text-sm text-gray-600">
                  Use Cohere Rerank API to improve search result relevance by 20-40%
                </p>
              </div>

              {/* Enable/Disable Toggle */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.reranking_enabled}
                  onChange={(e) => setSettings({ ...settings, reranking_enabled: e.target.checked })}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {settings.reranking_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>

            {/* Settings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reranking Model
                </label>
                <input
                  type="text"
                  value={settings.reranking_model}
                  onChange={(e) => setSettings({ ...settings, reranking_model: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="rerank-v3.5"
                  disabled={!settings.reranking_enabled}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cohere reranking model (e.g., rerank-v3.5)
                </p>
              </div>

              {/* Minimum Results Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center justify-between">
                    <span>Minimum Results Threshold</span>
                    <span className="text-blue-600 font-semibold">{settings.reranking_threshold}</span>
                  </div>
                </label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={settings.reranking_threshold}
                  onChange={(e) => setSettings({ ...settings, reranking_threshold: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  disabled={!settings.reranking_enabled}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only rerank when results ≥ {settings.reranking_threshold} chunks
                </p>
              </div>

              {/* Top K Results */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center justify-between">
                    <span>Top K Results</span>
                    <span className="text-blue-600 font-semibold">{settings.reranking_top_k}</span>
                  </div>
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  value={settings.reranking_top_k}
                  onChange={(e) => setSettings({ ...settings, reranking_top_k: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  disabled={!settings.reranking_enabled}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Return top {settings.reranking_top_k} most relevant results after reranking
                </p>
              </div>

              {/* Cost Estimate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cost Per Search
                </label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                  <DollarSign className="w-4 h-4 text-gray-500" />
                  <span className="font-semibold text-gray-900">{estimateCostPerSearch()}</span>
                  <span className="text-sm text-gray-500">per search</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Fixed cost regardless of document count (up to 100)
                </p>
              </div>
            </div>

            {/* Info Box */}
            {settings.reranking_enabled && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">How Reranking Works</h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Only activates when query is NOT empty (length &gt; 3 chars)</li>
                      <li>• Runs after vector search but before sending to LLM</li>
                      <li>• Reorders results by semantic relevance to improve answer quality</li>
                      <li>• Gracefully falls back to vector scores if API fails</li>
                      <li>• Cost tracked in LLM usage analytics</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Cost Projections */}
            {settings.reranking_enabled && (
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <TrendingUp className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Monthly Cost Estimates</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">1,000 searches/mo</div>
                        <div className="font-semibold text-gray-900">{estimateMonthlyCost(1000)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">10,000 searches/mo</div>
                        <div className="font-semibold text-gray-900">{estimateMonthlyCost(10000)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">100,000 searches/mo</div>
                        <div className="font-semibold text-gray-900">{estimateMonthlyCost(100000)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <Button
            onClick={handleReset}
            variant="outline"
            disabled={!hasChanges || isSaving}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Changes
          </Button>

          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 font-medium">
                You have unsaved changes
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
